import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { SidebarRail, type RailNavItem } from "@/components/layout/sidebar-rail";
import { TopBar } from "@/components/layout/top-bar";
import { MobileNavDrawer } from "@/components/layout/mobile-nav-drawer";

type Props = {
  brandTo: string;
  primaryNav: RailNavItem[];
  userMenu: React.ReactNode;
  searchPlaceholder?: string;
  /** "/app/projects" or "/admin/projects" — enables real global project search in TopBar. Omit for superadmin (not a workspace member, GET /api/editor/projects would 403). */
  projectsBasePath?: string;
};

/**
 * Frame.io-style app chrome: fixed icon rail on the left (brand, primary nav,
 * notifications, help, account menu) + a centered search top bar + content
 * area rendered via <Outlet/>. Shared by app-layout, admin-layout, and
 * superadmin-layout — only the nav items and notifications visibility differ
 * per role.
 *
 * NOTE: SearchOverlayProvider is expected to wrap AppShell at the layout
 * level (see layouts/app-layout.tsx and admin-layout.tsx). That's because
 * the rail's Search action needs the same context that project/folder pages
 * consume — the provider has to sit above BOTH.
 *
 * Mobile: the desktop rail is hidden below md. The TopBar renders a hamburger
 * (opens MobileNavDrawer with the same nav items), a centered workspace name,
 * and a corner search icon that navigates to /search (mobile pattern).
 */
export function AppShell({
  brandTo,
  primaryNav,
  userMenu,
  searchPlaceholder,
  projectsBasePath,
}: Props) {
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();

  // Immersive pages own their full chrome (their own top bar + sidebar), so the
  // shared TopBar is suppressed there: the project interior, its All Shares and
  // Edit Share pages, folder detail, AND the versions page (2026-08-17 — folder
  // + versions pages now reuse the project shell so the sidebar/top bar persist).
  const immersive = /^\/(app|admin)\/(projects\/[^/]+(\/(shares(\/[^/]+)?|folders\/[^/]+))?|versions\/[^/]+)$/.test(pathname);

  return (
    <div className="flex min-h-screen bg-sidebar text-foreground">
      <div className="hidden md:contents">
        <SidebarRail brandTo={brandTo} primaryNav={primaryNav} userMenu={userMenu} />
      </div>

      <MobileNavDrawer
        open={navOpen}
        onClose={() => setNavOpen(false)}
        brandTo={brandTo}
        primaryNav={primaryNav}
        userMenu={userMenu}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {!immersive && (
          <div className="overflow-hidden bg-background">
            <TopBar
              searchPlaceholder={searchPlaceholder}
              projectsBasePath={projectsBasePath}
              onOpenNav={() => setNavOpen(true)}
            />
          </div>
        )}
        <main className="flex-1 overflow-hidden bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
